// Custom Fields Modal

// Default
import { useState } from "react";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// Custom
import ContainedButton from "../../forms/button/ContainedButton";

// Utils
import { ToastState } from "../../types";
import { Button } from "@mui/material";
import { fetchPOSTRequest } from "../../utils/services";

const SampleFileData = [
  {
    MeterSerialNumber: "",
    IP_MeterTime: "2025-07-24T11:30:00Z",
    IP_Current: "10.5",
    IP_Voltage: "230",
    IP_Frequency: "50",
    IP_NeutralCurrent: "0.2",
    IP_PowerFactor: "0.98",
    IP_KW: "2.3",
    IP_KVA: "2.4",
    IP_KWH: "239.99",
    IP_KWHEXP: "0.0",
    IP_KVAH: "1250.00",
    IP_KVAHEXP: "0.0",
    IP_MDKW: "3.5",
    IP_MDKVA: "3.6",
    IP_MDResetCount: "1",
    IP_ProgramCount: "2",
    IP_TamperCount: "0",
    IP_TotalPowerOnDuration: "3600",
    IP_RelayStatus: "Closed",
    IP_LoadLimitValue: "5.0",
    Project_ID: "",
  },
  {
    MeterSerialNumber: "",
    IP_MeterTime: "2025-07-24T11:45:00Z",
    IP_Current: "5.5",
    IP_Voltage: "23",
    IP_Frequency: "50",
    IP_NeutralCurrent: "0.2",
    IP_PowerFactor: "0.8",
    IP_KW: "2.3",
    IP_KVA: "2.4",
    IP_KWH: "9.2",
    IP_KWHEXP: "0.0",
    IP_KVAH: "150.00",
    IP_KVAHEXP: "0.0",
    IP_MDKW: "3.5",
    IP_MDKVA: "3.0",
    IP_MDResetCount: "1",
    IP_ProgramCount: "2",
    IP_TamperCount: "0",
    IP_TotalPowerOnDuration: "3600",
    IP_RelayStatus: "Closed",
    IP_LoadLimitValue: "5.0",
    Project_ID: "",
  },
];

const parseCSV = (csvText: string): any[] => {
  const lines = csvText.trim().split("\n");
  const headers = lines[0]
    .split(",")
    .map((header) => header.trim().replace(/^"|"$/g, ""));

  const data = lines.slice(1).map((line) => {
    const values = line
      .split(",")
      .map((value) => value.trim().replace(/^"|"$/g, ""));
    const obj: Record<string, string> = {};
    headers.forEach((header, i) => {
      obj[header] = values[i] ?? "";
    });
    return obj;
  });

  return data;
};

const CreateReadValueModel = ({
  meterId,
  onClose,
  onSuccess,
  setToast,
}: {
  meterId: string;
  onClose: () => void;
  onSuccess: () => void;
  setToast: (toast: ToastState) => void;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [meterData, setMeterData] = useState<any[]>([]);

  const handleFileChange = (file: File | null) => {
    if (!file) return setMeterData([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (typeof text === "string") {
        const parsed = parseCSV(text);
        setMeterData(parsed);
        console.log("Parsed meter data:", parsed);
      }
    };
    reader.readAsText(file);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      console.log("Excel file selected:", file);
      setSelectedFile(file);
      handleFileChange(file);
    } else {
      setSelectedFile(null);
      setMeterData([]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    multiple: false,
    maxFiles: 1,
    onError: (error) => {
      console.log(error);
    },
  });

  const downloadCSV = () => {
    const data = SampleFileData;
    const csvRows: string[] = [];

    // Headers
    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(","));

    // Data rows
    for (const row of data) {
      const values = headers.map((header) => {
        const val = row[header as keyof typeof row];
        return `"${val}"`; // Wrap each value in quotes for safety
      });
      csvRows.push(values.join(","));
    }

    // Combine all into one CSV string
    const csvString = csvRows.join("\n");

    // Create a blob and trigger download
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_upload_reads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const uploadReads = async () => {
    const data = {
      Status: true,
      ApiName: "",
      BatchId: "",
      ProjectName: "",
      Data: {
        Command: "EditInstantaneousProfile",
        Parameters: {
          SerialNumber: meterId,
          Value: meterData,
        },
      },
      Error: [],
    };

    try {
      await fetchPOSTRequest("hes/response", data);

      setToast({
        message: "Reads uploaded successfully",
        severity: "success",
      });

      onSuccess();
    } catch (error) {
      console.error(error);
      setToast({
        message: "Failed to upload reads",
        severity: "error",
      });
    }
  };

  return (
    <Dialog
      onClose={onClose}
      open={true}
      maxWidth={false}
      sx={{
        "& .MuiPaper-root": {
          width: "500px",
          maxWidth: "none",
        },
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle>Upload Reads</DialogTitle>
        <IconButton onClick={onClose} sx={{ marginRight: "10px" }}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent dividers>
        <Stack direction={"column"} gap={"20px"}>
          <Stack direction={"row"} justifyContent={"flex-end"}>
            <Button
              variant={"text"}
              color={"primary"}
              sx={{ textDecoration: "underline", textTransform: "none" }}
              onClick={downloadCSV}
            >
              Doanwload Sample File
            </Button>
          </Stack>

          <div className="w-full max-w-md mx-auto">
            {selectedFile ? (
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <p>{selectedFile.name}</p>
                <Button
                  variant={"text"}
                  color={"primary"}
                  sx={{ textTransform: "none" }}
                  onClick={() => setSelectedFile(null)}
                >
                  Remove
                </Button>
              </Stack>
            ) : (
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition 
          ${isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-white hover:border-blue-400"}`}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon className="h-10 w-10 text-blue-500 mb-3" />
                <p className="text-gray-700 text-sm text-center">
                  {isDragActive
                    ? "Drop the CSV file here..."
                    : "Drag and drop or click to select a CSV file"}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Only .csv file is accepted
                </p>
              </div>
            )}
          </div>
          <ContainedButton
            label={"Upload"}
            sx={{
              width: "100%",
              fontWeight: 600,
              color: "#ffffff",
            }}
            onClick={uploadReads}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CreateReadValueModel;
